import {
    getAllKpiContexts,
    getKpiValueKey,
} from "@features/report/activity-entry/utils/activityEntryGroupHelpers";
import type { ActivityEntryFormGroup } from "@features/report/activity-entry/types/activityEntry";
import {
    isCalculatedEntryMode,
    normalizeValueForInput,
} from "@core/reports/utils/fieldTypeUtils";

/**
 * Merge server form values into local state after a draft/submit reload.
 * Non-dirty keys adopt the server value; dirty keys keep the local edit.
 * Keys in `forceKeys` (e.g. calculated KPIs) always adopt the server value.
 */
export function mergeServerFormValues({
    currentValues,
    baseline,
    serverValues,
    forceKeys,
}: {
    currentValues: Record<string, string>;
    baseline: Record<string, string>;
    serverValues: Record<string, string>;
    forceKeys?: Iterable<string>;
}): { values: Record<string, string>; baseline: Record<string, string> } {
    const forced = forceKeys ? new Set(forceKeys) : null;
    const nextValues = { ...currentValues };
    const nextBaseline = { ...baseline };

    for (const key of Object.keys(serverValues)) {
        const local = (currentValues[key] ?? "").toString().trim();
        const saved = (baseline[key] ?? "").toString().trim();
        const server = serverValues[key] ?? "";

        if (forced?.has(key) || local === saved) {
            nextValues[key] = server;
            nextBaseline[key] = server;
        }
    }

    return { values: nextValues, baseline: nextBaseline };
}

/** Build the controlled-input values map from a form GET response. */
export function buildFormValuesFromGroups(
    groups: ActivityEntryFormGroup[],
    showInactive: boolean
): Record<string, string> {
    const map: Record<string, string> = {};
    for (const { subGroupId, activity, kpi } of getAllKpiContexts(groups, showInactive)) {
        const key = getKpiValueKey(subGroupId, activity.activity_id, kpi.kpi_id);
        map[key] = normalizeValueForInput(
            kpi.submission?.kpi_value,
            kpi.data_type?.data_type_name ?? ""
        );
    }
    return map;
}

/** Value keys for calculated KPIs in a form GET response. */
export function collectCalculatedValueKeys(
    groups: ActivityEntryFormGroup[],
    showInactive: boolean
): string[] {
    const keys: string[] = [];
    for (const { subGroupId, activity, kpi } of getAllKpiContexts(groups, showInactive)) {
        if (!isCalculatedEntryMode(kpi.entry_mode)) continue;
        keys.push(getKpiValueKey(subGroupId, activity.activity_id, kpi.kpi_id));
    }
    return keys;
}
